/** Shared client-side filter/sort helpers for list pages. */

export type SortDir = 'asc' | 'desc'

export interface SortOption {
  value: string
  label: string
}

export interface FilterOption {
  value: string
  label: string
}

export function compareSortValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b
  }

  const aStr = String(a).toLowerCase()
  const bStr = String(b).toLowerCase()
  if (aStr < bStr) return -1
  if (aStr > bStr) return 1
  return 0
}

export function sortItems<T>(
  items: T[],
  getValue: (item: T) => unknown,
  dir: SortDir = 'asc'
): T[] {
  const mult = dir === 'asc' ? 1 : -1
  return [...items].sort((a, b) => mult * compareSortValues(getValue(a), getValue(b)))
}

export function toggleSortDir(dir: SortDir): SortDir {
  return dir === 'asc' ? 'desc' : 'asc'
}

/** Inclusive date range filter value. Empty string means unbounded. Dates are YYYY-MM-DD. */
export interface DateRange {
  from: string
  to: string
}

export const EMPTY_DATE_RANGE: DateRange = { from: '', to: '' }

export function isDateRangeActive(range: DateRange | null | undefined): boolean {
  return !!range && (range.from !== '' || range.to !== '')
}

/**
 * Match a date-like value (YYYY-MM-DD or full ISO timestamp) against an inclusive range.
 * Inactive ranges match everything; missing values only match inactive ranges.
 */
export function matchesDateRange(value: unknown, range: DateRange | null | undefined): boolean {
  if (!isDateRangeActive(range)) return true
  if (value == null || value === '') return false
  const day = String(value).slice(0, 10)
  if (range!.from && day < range!.from) return false
  if (range!.to && day > range!.to) return false
  return true
}

/** Multi-select filter match. An empty selection matches everything. */
export function matchesMultiSelect(value: unknown, selected: string[]): boolean {
  if (selected.length === 0) return true
  if (value == null || value === '') return false
  return selected.includes(String(value))
}

export function toggleMultiSelectValue(selected: string[], value: string): string[] {
  return selected.includes(value)
    ? selected.filter((v) => v !== value)
    : [...selected, value]
}

/** Build sorted, de-duplicated FilterOptions from raw data values (for data-derived filters). */
export function uniqueFilterOptions(
  values: Array<string | null | undefined>,
  labelFor?: (value: string) => string
): FilterOption[] {
  const unique = Array.from(
    new Set(values.filter((v): v is string => typeof v === 'string' && v.trim() !== ''))
  )
  unique.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
  return unique.map((value) => ({ value, label: labelFor ? labelFor(value) : value }))
}
